import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Plane } from "lucide-react";
import { Button } from "@/components/ui/button";

const navLinks: { label: string; href: string; highlight?: boolean }[] = [
  { label: "Videos", href: "#videos" },
  { label: "B737 MAX", href: "#b737", highlight: true },
  { label: "Products", href: "#products" },
  { label: "Features", href: "#features" },
  { label: "Specs", href: "#specs" },
  { label: "Pricing", href: "#pricing" },
  { label: "Contact", href: "#contact" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const go = (href: string) => {
    setMobileOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 35 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 border-b border-border shadow-sm" : "bg-white/80"
      }`}
    >
      <div className="container mx-auto px-6 h-16 flex items-center justify-between">
        <a href="#" onClick={e => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          className="flex items-center gap-2.5 group">
          <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center shadow-[0_2px_8px_oklch(0.28_0.09_240/0.3)] group-hover:shadow-[0_4px_14px_oklch(0.28_0.09_240/0.45)] transition-shadow">
            <Plane className="w-5 h-5 text-white rotate-45" />
          </div>
          <span className="text-xl font-black tracking-tight text-foreground" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            EFIS<span style={{ color: "oklch(0.55 0.22 25)" }}>FLY</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map(l => (
            <button key={l.label} onClick={() => go(l.href)}
              className={`px-4 py-2 text-sm rounded-md transition-all font-medium ${
                l.highlight
                  ? "font-bold"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`}
              style={l.highlight ? { color: "oklch(0.55 0.22 25)", background: "oklch(0.55 0.22 25 / 0.08)" } : undefined}
              onMouseEnter={l.highlight ? e => (e.currentTarget.style.background = "oklch(0.55 0.22 25 / 0.15)") : undefined}
              onMouseLeave={l.highlight ? e => (e.currentTarget.style.background = "oklch(0.55 0.22 25 / 0.08)") : undefined}
            >
              {l.highlight ? `⚡ ${l.label}` : l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex">
          <Button size="sm" className="bg-primary text-primary-foreground font-semibold shadow-[0_2px_8px_oklch(0.28_0.09_240/0.35)] hover:shadow-[0_4px_14px_oklch(0.28_0.09_240/0.45)] transition-shadow"
            onClick={() => go("#pricing")}>
            Get License
          </Button>
        </div>

        <button onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden p-2 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-all">
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-b border-border">
            <div className="container mx-auto px-6 py-4 flex flex-col gap-1">
              {navLinks.map(l => (
                <button key={l.label} onClick={() => go(l.href)}
                  className="text-left px-4 py-3 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md transition-all font-medium">
                  {l.label}
                </button>
              ))}
              <Button className="mt-2 bg-primary text-primary-foreground font-semibold" onClick={() => go("#pricing")}>
                Get License
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
